import { memo } from 'react';
import PropTypes from 'prop-types';
import { Box } from '@mui/material';
import { IconsList } from './IconsList';

/**
 * Dynamically renders a Material-UI icon based on the provided `icon` prop.
 * Supports both filled and outlined versions of icons by appending 'Outlined' to the icon name if `filled` is `false`.
 * Renders a `Box` component displaying the icon name as a fallback if the specified icon does not exist in the MUI icon library.
 * Additional styles can be applied through the `sx` prop.
 *
 * @param {object} props - Component props.
 * @param {string} [props.className] - An optional class name to apply to the icon container.
 * @param {string} props.color - The color of the icon. Required.
 * @param {string} props.icon - The name of the icon in snake_case, which is converted to PascalCase for resolution.
 * @param {boolean} [props.filled=true] - Specifies if the icon should be filled or outlined. Outlined icons are used if `false`.
 * @param {object} [props.sx] - Optional style overrides for the `Box` component containing the icon.
 * @param {object} other - Other props to spread onto the `Box` component.
 * @returns {JSX.Element} A Material-UI icon wrapped in a `Box`, or a `Box` containing the icon name as text if the icon is not found.
 */

const IconResolver = (props) => {
  const { className = '', color = null, icon = '', filled = false, fontSize = null, sx } = props;

  const key = filled ? icon : `${icon}_outlined`;
  const IconComponent = IconsList[key];

  if (IconComponent) {
    return <IconComponent sx={styleWrapper(fontSize, color, sx)} />;
  }

  // Fallback si icône non trouvée (ex: typo ou oubli dans listIcons)
  return (
    <Box component="span" className={className} sx={styleWrapper(fontSize, color, sx, true)}>
      {icon}
    </Box>
  );
};

const styleWrapper = (fontSize, color, sx, customIcon) => ({
  flex: '0 0 auto',
  fontSize,
  width: fontSize,
  height: fontSize,
  color,
  border: customIcon ? '1px solid transparent' : null,
  svg: { height: '100%', width: '100%' },
  ...sx
});

IconResolver.propTypes = {
  className: PropTypes.string,
  color: PropTypes.string,
  icon: PropTypes.string.isRequired,
  filled: PropTypes.bool,
  fontSize: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  sx: PropTypes.object
};

export default memo(IconResolver);
